import { z } from 'zod';

/**
 * DM-1 — Docs module Zod schemas (BACKLOG-2026-05-29-docs-module.md).
 *
 * SSOT для документации STAITER. Используется и backend (валидация
 * payload'ов API), и frontend (типизация query/mutation hooks).
 */

export const DocLocale = z.enum(['ru', 'en', 'kk']);
export type DocLocale = z.infer<typeof DocLocale>;

export const DocRole = z.enum([
  'manager',
  'accountant',
  'reviewer',
  'admin',
  'viewer',
  'warehouse-operator',
  'shared',
]);
export type DocRole = z.infer<typeof DocRole>;

/**
 * Slug = относительный путь от `docs/user-guide/` без расширения.
 * Примеры: `accountant/03-stock-entries`, `manager/02-hero-metrics`,
 * `shared/login-2fa`, `glossary`.
 *
 * Регекс допускает:
 *  - role/NN-name (manager/02-hero-metrics)
 *  - shared/name (shared/login-2fa)
 *  - стандалон (glossary, README)
 */
const SLUG_RE =
  /^(?:(?:manager|accountant|reviewer|admin|viewer|warehouse-operator|shared)\/[a-z0-9-]+(?:\/[a-z0-9-]+)?|glossary|readme)$/i;

export function assertSlugFormat(slug: string): void {
  if (!SLUG_RE.test(slug)) {
    throw new Error(
      `Invalid doc slug: "${slug}". Expected role/NN-name, shared/name, glossary, or readme.`,
    );
  }
}

export const DocPage = z.object({
  id: z.string().uuid(),
  slug: z.string().min(1).max(120),
  role: DocRole,
  section: z.string().max(60).nullable(),
  order_idx: z.number().int().min(0),
  locale: DocLocale,
  title: z.string().min(1).max(200),
  body_md: z.string(),
  body_html: z.string(),
  version: z.number().int().min(1),
  is_overridden: z.boolean(),
  related: z.array(z.string()),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
  updated_by: z.string().uuid().nullable(),
});
export type DocPage = z.infer<typeof DocPage>;

/** Запись в дереве `/api/v1/docs` — облегчённая, без body_md/html. */
export const DocPageSummary = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  role: DocRole,
  section: z.string().nullable(),
  order_idx: z.number().int(),
  locale: DocLocale,
  title: z.string(),
  version: z.number().int(),
  updated_at: z.string().datetime({ offset: true }),
  is_new_for_user: z.boolean(),
});
export type DocPageSummary = z.infer<typeof DocPageSummary>;

export const DocTocSection = z.object({
  name: z.string(),
  pages: z.array(DocPageSummary),
});
export type DocTocSection = z.infer<typeof DocTocSection>;

export const DocsTocResponse = z.object({
  role: DocRole,
  locale: DocLocale,
  sections: z.array(DocTocSection),
});
export type DocsTocResponse = z.infer<typeof DocsTocResponse>;

/** Элемент оглавления (TOC) — h2/h3 с anchor. */
export const DocTocItem = z.object({
  level: z.number().int().min(2).max(4),
  id: z.string(),
  text: z.string(),
});
export type DocTocItem = z.infer<typeof DocTocItem>;

export const DocScreenshotResolved = z.object({
  id: z.string().uuid(),
  filename: z.string(),
  minio_key: z.string(),
  signed_url: z.string().min(1), // absolute URL (S3) либо относительный путь (local-fs fallback)
  alt: z.string(),
});
export type DocScreenshotResolved = z.infer<typeof DocScreenshotResolved>;

export const DocPageDetailResponse = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  role: DocRole,
  section: z.string().nullable(),
  locale: DocLocale,
  title: z.string(),
  body_html: z.string(),
  body_md: z.string(),
  version: z.number().int(),
  updated_at: z.string().datetime({ offset: true }),
  updated_by_email: z.string().nullable(),
  is_overridden: z.boolean(),
  is_fallback_locale: z.boolean(),
  related: z.array(
    z.object({
      slug: z.string(),
      title: z.string(),
    }),
  ),
  screenshots: z.array(DocScreenshotResolved),
  toc: z.array(DocTocItem),
  user_state: z.object({
    last_read_version: z.number().int().nullable(),
    is_new_for_user: z.boolean(),
  }),
});
export type DocPageDetailResponse = z.infer<typeof DocPageDetailResponse>;

/** PATCH `/api/v1/docs/:id` body. */
export const DocPageUpdateRequest = z
  .object({
    expected_version: z.number().int().min(1),
    body_md: z.string().min(1),
    reason: z.string().max(2000).optional(),
    force_overwrite: z.boolean().optional().default(false),
  })
  // zod-strict-ok: input request-body
  .strict();
export type DocPageUpdateRequest = z.infer<typeof DocPageUpdateRequest>;

export const DocPageUpdateOk = z.object({
  ok: z.literal(true),
  new_version: z.number().int(),
  updated_at: z.string().datetime({ offset: true }),
});
export type DocPageUpdateOk = z.infer<typeof DocPageUpdateOk>;

export const DocPageUpdateConflict = z.object({
  code: z.literal('version_conflict'),
  current_version: z.number().int(),
  conflicting_actor: z.object({
    id: z.string().uuid().nullable(),
    email: z.string().nullable(),
    saved_at: z.string().datetime({ offset: true }),
  }),
});
export type DocPageUpdateConflict = z.infer<typeof DocPageUpdateConflict>;
